import {
  Button,
  Flex,
  FlexColumn,
  FlexGrow,
  SelectInput,
  ToastStatus,
  useAsyncFunctionProgress,
  useFormConfigurationContext,
  useToastNotifications,
  useUserHasRole,
} from '@equifinance/shared';
import styled, { } from 'styled-components';
import moment from 'moment';
import { useState } from 'react';
import { formatDateTime } from '@equifinance/shared/utils';
import {
  SendOfferDetailsStatus,
  useAccountContext,
  useChecklistContext,
  VerificationType,
} from '../../../../../../../../contexts';
import { ChecklistSection } from '../../../../../../ChecklistPage';
import { GenericSection } from '../../../../../GenericSection';
import {
  DataInfo, HeaderCss, LinkButton, RowData, TitleCss,
} from '../../../../common';

const EsigSectionWrapper = styled(FlexColumn)`${({ theme }) => `
  padding: ${theme.padding[1]} ${theme.padding[9]};
  gap: ${theme.padding[5]};
`}`;

const Verifications = styled(Flex)`${({ theme }) => `
  padding: 0 ${theme.padding[5]};
  gap: 25%;
`}`;

const EmailIcon = styled.i`${({ theme }) => `
  color: ${theme.colours.link[1]};
  padding-right: ${theme.padding[1]};
  font-size: ${theme.fontSize[3]};
  display: inline-block;
`}`;

export default function EsigSection() {
  const { account } = useAccountContext();
  const { checklist, sendOffer: postSendOffer, resendOffer } = useChecklistContext();
  const { sendOffer } = checklist ?? {};

  const {
    status, createdDate, expiryDate, completedDate,
  } = sendOffer?.offer ?? {};
  const [app1Verification, setApp1Verification] = useState('EQUIFAX_PASSED');
  const [app2Verification, setApp2Verification] = useState('EQUIFAX_PASSED');

  const applicant1 = account?.applicants[0];
  const applicant2 = account?.applicants[1];

  const { isDisabled } = useFormConfigurationContext();
  const { addToastNotification } = useToastNotifications();

  const onNewSession = async () => {
    const verificationTypes = [applicant1 && app1Verification, applicant2 && app2Verification].filter(Boolean);
    await postSendOffer(verificationTypes);
  };

  const { inProgress: newSessionProgress, execute: executeNewSession } = useAsyncFunctionProgress(onNewSession);

  const onResendOffer = async () => {
    if (isDisabled) return;

    await resendOffer();
    addToastNotification({
      text: 'Email successfully re-sent',
      type: ToastStatus.SUCCESS,
      showCloseIcon: true,
    });
  };

  const { inProgress: resendOfferProgress, execute: executeResendOffer } = useAsyncFunctionProgress(onResendOffer);

  const userCantOfferCase = !useUserHasRole('CAN_OFFER_CASE');
  const disableNewSession = userCantOfferCase || newSessionProgress;
  const disableReSendEmail = userCantOfferCase || resendOfferProgress;

  return (
    <GenericSection
      id="esig"
      subSection={ChecklistSection.SEND_OFFER_OFFER_ESIG}
      level={1}
      headerCss={HeaderCss}
      titleCss={TitleCss}
    >
      <EsigSectionWrapper>
        {!status && (
          <>
            <Verifications>
              <FlexGrow>
                <SelectInput
                  id={`${applicant1.id}-verification`}
                  required
                  label="Applicant 1 ID Verification:"
                  value={app1Verification}
                  placeholder="Select"
                  options={VerificationType}
                  onChange={(v) => { setApp1Verification(v); }}
                />
              </FlexGrow>
              <FlexGrow>
                {applicant2 && (
                  <SelectInput
                    id={`${applicant2.id}-verification`}
                    required
                    label="Applicant 2 ID Verification:"
                    value={app2Verification}
                    placeholder="Select"
                    options={VerificationType}
                    onChange={(v) => { setApp2Verification(v); }}
                  />
                )}
              </FlexGrow>
            </Verifications>
            <Flex>
              <Button disabled={disableNewSession} small onClick={executeNewSession}>New Session</Button>
            </Flex>
          </>
        )}
        <RowData justifyContent="space-between">
          <DataInfo label="E-Signature Status" value={status && SendOfferDetailsStatus[status]} />
          {status && ['Created', 'InProgress'].includes(status) && (
            <Flex>
              <LinkButton disabled={disableReSendEmail} onClick={executeResendOffer}>
                <EmailIcon className="mi-email" />
                Re-send Customer Email
              </LinkButton>
            </Flex>
          )}
        </RowData>
        <RowData>
          <DataInfo label="Date Session Started" value={createdDate && formatDateTime(createdDate, 'DD/MM/YYYY')} />
          <DataInfo label="Date Session Completed" value={status === 'Complete' && formatDateTime(completedDate, 'DD/MM/YYYY')} />
          <DataInfo
            label="Date Session Expires"
            value={(status !== 'Complete'
              && expiryDate
              && moment().isAfter(expiryDate))
              && formatDateTime(expiryDate, 'DD/MM/YYYY')}
          />
          <DataInfo
            label="Date Session Expired"
            value={(status !== 'Complete'
              && expiryDate
              && moment(expiryDate).isSameOrAfter(moment()))
              && formatDateTime(expiryDate, 'DD/MM/YYYY')}
          />
        </RowData>
      </EsigSectionWrapper>
    </GenericSection>
  );
}
